import React, { useEffect, useState, useRef } from 'react';
import { loadYouTubeApi } from '../utils/youtubeApi';
import { loadSoundCloudApi } from '../utils/soundCloudApi';
import { setActivePlayer } from '../utils/audioManager';
import '../styles/PastEventCard.css';

const PastEventCard = ({ event }) => {
  const [embedError, setEmbedError] = useState(null);
  const audioContextRef = useRef(null);
  
  // Use a stable unique ID - prefer database id, then event_id, then generate one
  const uniqueId = useRef(event.id || event.event_id || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const cardId = uniqueId.current;

  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    try {
      if (url.includes('youtu.be')) {
        return url.split('youtu.be/')[1].split('?')[0];
      }
      if (url.includes('youtube.com')) {
        const urlParams = new URLSearchParams(url.split('?')[1]);
        return urlParams.get('v');
      }
      return null;
    } catch (err) {
      console.error('Error parsing YouTube URL:', err);
      return null;
    }
  };

  const handleUserInteraction = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      console.log('AudioContext created');
    } else {
      audioContextRef.current.resume().then(() => {
        console.log('AudioContext resumed');
      });
    }
  };

  useEffect(() => {
    // Determine which link to use (soundcloud or youtube)
    const featured_mix = event.soundcloud_link || event.youtube_link;
    if (!featured_mix) return;

    const youtubeId = getYouTubeVideoId(featured_mix);
    
    if (youtubeId) {
      // YouTube link
      loadYouTubeApi(() => createYouTubePlayer(youtubeId));
    } else {
      // SoundCloud link
      loadSoundCloudApi(() => {
        const playerId = `soundcloud-player-${cardId}`;
        const playerElement = document.getElementById(playerId);
        
        if (!playerElement) {
          console.error(`Player element with ID ${playerId} not found.`);
          setEmbedError('SoundCloud player element not found.');
          return;
        }

        const player = SC.Widget(playerElement);
        
        player.bind(SC.Widget.Events.READY, () => {
          console.log('SoundCloud player loaded');
          setActivePlayer({
            pause: () => player.pause()
          }, 'soundcloud');
        });

        player.bind(SC.Widget.Events.ERROR, (error) => {
          console.error('SoundCloud player error:', error);
          setEmbedError('Failed to load SoundCloud player');
        });
      });
    }
  }, [event.youtube_link, event.soundcloud_link]);


  const createYouTubePlayer = (videoId) => {
    try {
      if (!videoId) {
        console.error('Invalid YouTube video ID');
        setEmbedError('Invalid YouTube video ID');
        return;
      }

      // Check if the element exists
      const playerContainerId = `youtube-player-${cardId}`;
      const playerElement = document.getElementById(playerContainerId);
      if (!playerElement) {
        console.error(`Player element with ID ${playerContainerId} not found`);
        setEmbedError('YouTube player element not found');
        return;
      }

      console.log(`Creating YouTube player for video ID: ${videoId}`);
      
      // Clear any existing content in the container
      playerElement.innerHTML = '';
      
      // Create a new iframe element directly as per YouTube documentation
      const iframe = document.createElement('iframe');
      iframe.id = `youtube-iframe-${cardId}`;
      iframe.type = 'text/html';
      iframe.width = '100%';
      iframe.height = '100%';
      iframe.frameBorder = '0';
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
      iframe.allowFullscreen = true;
      
      // Set the src with all required parameters
      const params = new URLSearchParams({
        autoplay: 0,
        modestbranding: 1,
        rel: 0,
        showinfo: 1,
        controls: 1,
        color: 'white',
        iv_load_policy: 3,
        origin: window.location.origin
      });
      
      iframe.src = `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
      
      // Add the iframe to the container
      playerElement.appendChild(iframe);
      
      // Set up message listener for YouTube iframe API events
      const handleYouTubeMessage = (event) => {
        try {
          if (event.origin !== 'https://www.youtube.com') return;
          const data = JSON.parse(event.data);
          
          if (data.event === 'onReady') {
            console.log('YouTube player ready');
          } else if (data.event === 'onStateChange' && data.info === 1) { // 1 = playing
            console.log('YouTube player started playing');
            setActivePlayer({
              pause: () => {
                // Send pause command to iframe
                iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
              }
            }, 'youtube');
          } else if (data.event === 'onError') {
            console.error('YouTube player error:', data.info);
            setEmbedError(`YouTube player error: ${data.info}`);
          }
        } catch (err) {
          console.error('Error handling YouTube message:', err);
        }
      };
      
      // Add and remove event listener
      window.addEventListener('message', handleYouTubeMessage);
      
      // Clean up function to remove event listener when component unmounts
      return () => {
        window.removeEventListener('message', handleYouTubeMessage);
      };
    } catch (error) {
      console.error('Error creating YouTube player:', error);
      setEmbedError('Failed to load YouTube player');
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const renderMedia = () => {
    // Determine which link to use (soundcloud or youtube)
    const featured_mix = event.soundcloud_link || event.youtube_link;
    
    if (!featured_mix || embedError) return null;

    console.log('Featured Mix URL:', featured_mix);

    const youtubeId = getYouTubeVideoId(featured_mix);
    console.log('YouTube ID:', youtubeId);

    if (youtubeId) {
      return (
        <div className="media-container">
          <div className="youtube-player" style={{
            position: 'relative',
            paddingBottom: '56.25%', /* 16:9 aspect ratio */
            height: 0,
            overflow: 'hidden',
            maxWidth: '100%',
            borderRadius: '8px'
          }}>
            <div 
              id={`youtube-player-${cardId}`}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%'
              }}
            ></div>
          </div>
        </div>
      );
    } else {
      const encodedUrl = encodeURIComponent(featured_mix);
      const playerId = `soundcloud-player-${cardId}`;
      
      return (
        <div className="media-container">
          <div className="soundcloud-player" style={{
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            position: 'relative',
            height: '166px',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.5)'
            }} />
            <iframe
              id={playerId}
              title="SoundCloud Player"
              width="100%"
              height="166"
              scrolling="no"
              frameBorder="no"
              allow="autoplay"
              style={{ position: 'relative', zIndex: 1 }}
              src={`https://w.soundcloud.com/player/?url=${encodedUrl}&color=%23ff5500&auto_play=false&hide_related=true&show_comments=true&show_user=true&show_reposts=false&show_teaser=false&visual=true`}
            />
          </div>
        </div>
      );
    }
  };

  return (
    <div className="past-event-card" onClick={handleUserInteraction}>
      {renderMedia()}
      <div className="past-event-content">
        <div className="past-event-header">
          <h3>{event.event_id ? `${event.event_name} ${String(event.event_id).padStart(3, '0')}` : event.event_name}</h3>
          <div className="event-meta">
            <div className="event-artists">
              {event.featured_artist_names.length === 1 && event.featured_artist_names[0] === 'TBA' 
                ? '' 
                : event.featured_artist_names.join(' • ')}
            </div>
            <div className="event-date">
              {formatDate(event.event_start)}
              {event.description && ' • '}
              {event.description}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PastEventCard;
